import * as yup from "yup";

import {
    Alert,
    Box,
    Container,
    Grid,
    Tab,
    Tabs,
    TextField,
    Typography,
} from "@mui/material";
import {
    LinkAction,
    LinkRegistry,
    PermissionResourceType
} from "../../../models";
import {
    useMemo,
    useState
} from "react";

import { AlertMessage } from "../../../lib/util/AlertMessage";
import { AttachmentParent } from "../../../componentsV2/image/AttachmentParent";
import { ExternalReportLinkRegistryFormVariables } from "@tenera/linkregistrymodels";
import { GraphQLLinkRegistryDAOFactory } from "../../../lib/linkRegistry/dao/GraphQLLinkRegistryDAOFactory";
import { LinkRegistryDAO } from "../../../lib/linkRegistry/dao/LinkRegistryDAO";
import LoadingButton from "@mui/lab/LoadingButton";
import { Location } from "../../../models";
import LocationSearchBox from "../../../componentsV2/location/LocationSearchBox";
import PropertySearchBox from "../../../componentsV2/property/PropertySearchBox";
import { QRCodeScannerToggle } from "../scanner/QRCodeScannerToggle";
import { StateContext } from "../../../lib/design/document/state/StateContext";
import { TemporaryImage } from "../../../componentsV2/image/TemporaryImage";
import { UploadStagingImageCarousel } from "../../../componentsV2/image/UploadStagingImageCarousel";
import _ from "lodash";
import { externalReportLinkRegistryVariablesSchema } from "../../../lib/linkRegistry/schema/variables/ExternalReportLinkRegistryVariablesSchema";
import theme from "../../../assets/style/theme";
import { useCreateLocation } from "../../../lib/location/hook/useCreateLocation";
import { useFormik } from "formik";
import { useUploadImages } from "../../../componentsV2/image/carouselComponent/useUploadImage";

type SimplifiedLinkRegistryFormProps = {
    id: string;
    linkRegistry: LinkRegistry | undefined;
    onSaved: () => (void | Promise<void>);
    isSubmitted: boolean;
    setIsSubmitted: React.Dispatch<React.SetStateAction<boolean>>;
    setHasFormChanged: React.Dispatch<React.SetStateAction<boolean>>;
};

type SimplifiedLinkRegistryFormData = yup.InferType<typeof externalReportLinkRegistryVariablesSchema> & {
    createLocation: boolean;
    newLocationName?: string;
};

enum LinkProvisioningTabs {
    NEW_LOCATION = 'New Location',
    EXISTING_LOCATION = 'Use Existing Location'
}

const linkRegistryDAO: LinkRegistryDAO = GraphQLLinkRegistryDAOFactory.getInstance();

export const PropertyManagerLinkRegistryForm = (props: SimplifiedLinkRegistryFormProps) => {
    const { id, linkRegistry, onSaved, isSubmitted, setIsSubmitted, setHasFormChanged } = props;

    const [alertMessage, setAlertMessage] = useState<AlertMessage | undefined>();
    const [newLocationImages, setNewLocationImages] = useState<Array<TemporaryImage>>([]);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const { createLocation } = useCreateLocation(StateContext.INSPECTION);
    const { uploadImages } = useUploadImages();
    const [activeTab, setActiveTab] = useState<LinkProvisioningTabs>(LinkProvisioningTabs.EXISTING_LOCATION);

    const initialValues = useMemo<SimplifiedLinkRegistryFormData>(() => {
        if (linkRegistry == undefined || linkRegistry.variables == undefined) {
            return {
                brandingId: '0',
                propertyId: '',
                propertyAddress: undefined,
                locationId: undefined,
                locationName: undefined,
                createLocation: false,
                newLocationName: undefined
            };
        }
        const formVariables = JSON.parse(linkRegistry.variables);
        return {
            brandingId: formVariables.brandingId ?? '0',
            propertyId: formVariables.propertyId ?? '',
            propertyAddress: formVariables.propertyAddress ?? undefined,
            locationId: formVariables.locationId ?? undefined,
            locationName: formVariables.locationName ?? undefined,
            createLocation: false,
            newLocationName: undefined
        };
    }, [linkRegistry]);

    const [lastSubmittedValues, setLastSubmittedValues] = useState<SimplifiedLinkRegistryFormData | undefined>(initialValues);

    const onSubmitForm = async (data: SimplifiedLinkRegistryFormData) => {
        try {
            setIsSubmitting(true);
            setAlertMessage(undefined);
            let locationId = data.locationId;
            let locationName = data.locationName;
            if (data.createLocation) {
                const location = await createLocation(
                    new Location({
                        propertyId: data.propertyId!,
                        name: data.newLocationName
                    }),
                    false
                );
                locationId = location.id;
                locationName = data.newLocationName;
                formik.setValues(
                    {
                        ...formik.values,
                        locationId: locationId
                    }
                );
                if (newLocationImages.length > 0) {
                    await uploadImages(
                        newLocationImages.map(image => image.file),
                        { shouldResize: true },
                        { id: locationId, type: PermissionResourceType.LOCATION } satisfies AttachmentParent,
                        data.propertyId
                    );
                    setNewLocationImages([]);
                }
            }
            const formVariables = {
                brandingId: data.brandingId,
                propertyId: data.propertyId,
                propertyAddress: data.propertyAddress,
                locationId: locationId,
                locationName: locationName
            } satisfies ExternalReportLinkRegistryFormVariables;

            // Persist issue submission form configuration for property manager
            const isConfiguredToIssueSubmissionForm = linkRegistry?.action === LinkAction.EXTERNAL_REPORT;
            const newLinkRegistry = {
                id: id,
                propertyId: data.propertyId,
                action: isConfiguredToIssueSubmissionForm ? LinkAction.EXTERNAL_REPORT : LinkAction.ASSESSMENT_FORM,
                variables: JSON.stringify(formVariables),
                published: true
            } satisfies LinkRegistry;
            setLastSubmittedValues({
                ...formik.values,
                locationId: locationId
            });
            setIsSubmitted(true);
            if (linkRegistry) {
                await linkRegistryDAO.update(newLinkRegistry);
                await onSaved();
                return;
            }
            await linkRegistryDAO.create(newLinkRegistry);
            await onSaved();
        } catch (error) {
            setAlertMessage({
                severity: "error",
                message: `Failed to ${linkRegistry ? "update" : "create"} link configuration: ${(error as Error).message}`
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const formik = useFormik<SimplifiedLinkRegistryFormData>({
        initialValues: initialValues,
        validationSchema: externalReportLinkRegistryVariablesSchema,
        onSubmit: onSubmitForm,
    });

    const hasFormChanged = useMemo(() => {
        const areEqual = _.isEqual(formik.values, lastSubmittedValues);
        setHasFormChanged(!areEqual);
        return !areEqual;
    }, [formik.values, lastSubmittedValues]);

    return (
        <Container component="main">
            {isSubmitted ?
                <Box
                    my={2}
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    gap={1}
                >
                    <Typography variant="h5">QR code configured successfully!</Typography>
                    <Typography variant="body1">
                        Write the location name onto the QR code sticker. The sticker is ready to be placed at the location.
                    </Typography>
                    <Typography variant="body1">
                        If you would like to submit an issue for this location, use your camera to scan the QR code.
                    </Typography>
                    <Box
                        display="flex"
                        alignItems="center"
                        gap={1}
                    >
                        <QRCodeScannerToggle
                            variant="outlined"
                        >
                            Configure Next QR Code
                        </QRCodeScannerToggle>
                    </Box>
                </Box> :
                <Box
                    my={2}
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                >
                    <Box
                        component="form"
                        onSubmit={formik.handleSubmit}
                        noValidate
                        sx={{ mt: 1 }}
                        width="100%"
                        maxWidth={theme.breakpoints.values["md"]}
                    >
                        <Grid container spacing={2}>
                            <Grid item xs={12} container justifyContent={{ xs: "center", md: "flex-start" }}>
                                <Typography variant="h6">
                                    Where will this QR code be placed?
                                </Typography>
                            </Grid>
                            <Grid container item xs={12} spacing={2} alignContent="flex-start" justifyContent={{ xs: "center", md: "flex-start" }}>
                                <Tabs
                                    value={activeTab}
                                    onChange={(_, newValue) => {
                                        formik.setValues(
                                            {
                                                ...formik.values,
                                                createLocation: newValue === LinkProvisioningTabs.NEW_LOCATION
                                            }
                                        );
                                        setActiveTab(newValue);
                                    }}
                                >
                                    <Tab
                                        value={LinkProvisioningTabs.EXISTING_LOCATION}
                                        label={LinkProvisioningTabs.EXISTING_LOCATION}
                                    />
                                    <Tab
                                        value={LinkProvisioningTabs.NEW_LOCATION}
                                        label={LinkProvisioningTabs.NEW_LOCATION}
                                    />
                                </Tabs>
                                <Grid item xs={12}>
                                    <PropertySearchBox
                                        name="propertyId"
                                        id="propertyId"
                                        label="Property"
                                        onClear={() => {
                                            formik.setValues(
                                                {
                                                    ...formik.values,
                                                    propertyId: '',
                                                    propertyAddress: undefined
                                                }
                                            );
                                        }}
                                        onSelect={(property) => {
                                            formik.setValues(
                                                {
                                                    ...formik.values,
                                                    propertyId: property?.id ?? "",
                                                    propertyAddress: property?.address ?? undefined
                                                }
                                            );
                                        }}
                                        initialPropertyId={formik.values.propertyId}
                                        error={Boolean(formik.errors.propertyId)}
                                        helperText={formik.errors.propertyId}
                                    />
                                </Grid>
                                {activeTab === LinkProvisioningTabs.NEW_LOCATION &&
                                    <>
                                        <Grid item xs={12}>
                                            <Typography variant="h6">Create New Location</Typography>
                                        </Grid>
                                        <Grid
                                            item
                                            xs={12}
                                            justifyItems="center"
                                        >
                                            <Box
                                                width={"100%"}
                                                maxWidth={theme.breakpoints.values["sm"]}
                                                height="auto"
                                                display="flex"
                                                sx={{ aspectRatio: "16 / 9" }}
                                            >
                                                <UploadStagingImageCarousel
                                                    imageDTOs={newLocationImages}
                                                    setImageDTOs={setNewLocationImages}
                                                />
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography variant="body2" color="textSecondary">
                                                Use a descriptive name that will help to identify where in the building the QR code is to be placed.
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                id="newLocationName"
                                                name="newLocationName"
                                                label="Location Name"
                                                value={formik.values.newLocationName}
                                                onChange={formik.handleChange}
                                                error={Boolean(formik.errors.newLocationName)}
                                                helperText={formik.errors.newLocationName}
                                            />
                                        </Grid>
                                    </>
                                }
                                {activeTab === LinkProvisioningTabs.EXISTING_LOCATION &&
                                    <>
                                        <Grid item xs={12}>
                                            <LocationSearchBox
                                                name="locationId"
                                                id="locationId"
                                                typeName="Location"
                                                propertyId={formik.values?.propertyId}
                                                label="Location"
                                                onClear={() => {
                                                    formik.setValues(
                                                        {
                                                            ...formik.values,
                                                            locationId: undefined,
                                                            locationName: undefined
                                                        }
                                                    );
                                                }}
                                                onSelect={(location) => {
                                                    formik.setValues(
                                                        {
                                                            ...formik.values,
                                                            locationId: location?.id,
                                                            locationName: location?.name ?? undefined
                                                        }
                                                    );
                                                }}
                                                onNewOptionCreated={async (location) => {
                                                    setActiveTab(LinkProvisioningTabs.NEW_LOCATION);
                                                    formik.setValues(
                                                        {
                                                            ...formik.values,
                                                            locationName: location.name ?? undefined,
                                                            createLocation: true
                                                        }
                                                    );
                                                }}
                                                disabled={formik.values.propertyId == undefined}
                                                initialLocationId={formik.values.locationId}
                                                error={Boolean(formik.errors.locationId)}
                                                helperText={formik.errors.locationId}
                                            />
                                        </Grid>
                                    </>
                                }

                            </Grid>
                            {alertMessage && (
                                <Grid item xs={12}>
                                    <Alert severity={alertMessage.severity}>{alertMessage.message}</Alert>
                                </Grid>
                            )}
                            <Grid item xs={12}>
                                <LoadingButton
                                    type="submit"
                                    fullWidth
                                    variant="contained"
                                    loading={isSubmitting}
                                    startIcon={<></>}
                                    loadingPosition="start"
                                    disabled={!formik.isValid || !hasFormChanged}
                                >
                                    Submit
                                </LoadingButton>
                            </Grid>
                        </Grid>
                    </Box>
                </Box>
            }
        </Container >
    );
};

